import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Wrench, FileText, Receipt, DollarSign, ArrowRight, Clock3 } from 'lucide-react'
import { fetchJobs, fetchQuotes, fetchInvoices } from '../lib/api'
import { formatCurrency } from '@shopbay/shared'
import { ApprovedReadyBadge, isJobReadyToStart } from '../components/jobs/ApprovedReadyBadge'
import { formatVehicleLabel } from '../lib/vehicleLabel'
import { getJobDisplayTitle } from '../lib/jobDisplayTitle'
import { useShopId } from '../hooks/useShopId'
import { usePageTitle } from '../hooks/usePageTitle'
import { useIsMobile } from '../hooks/useMediaQuery'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Skeleton } from '../components/ui/Skeleton'
import { EmptyState } from '../components/ui/EmptyState'
import { Button } from '../components/ui/Button'
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/Table'

export function Dashboard() {
  const shopId = useShopId()
  const isMobile = useIsMobile()
  usePageTitle('Dashboard')

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', shopId],
    queryFn: () => fetchJobs(shopId),
  })

  const { data: quotes, isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes', shopId],
    queryFn: () => fetchQuotes(shopId),
  })

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', shopId],
    queryFn: () => fetchInvoices(shopId),
  })

  const recentJobs = jobs?.slice(0, 5) || []
  const openJobs = jobs?.filter((j: any) => j.status === 'open' || j.status === 'in_progress') || []
  const waitingParts = jobs?.filter((j: any) => j.status === 'waiting_parts') || []
  const pendingQuotes = quotes?.filter((q: any) => q.status === 'sent' || q.status === 'viewed') || []
  const unpaidInvoices = invoices?.filter((i: any) => i.status === 'sent' || i.status === 'overdue') || []
  const paidThisMonth = invoices?.filter((i: any) => {
    if (i.status !== 'paid') return false
    const paid = new Date(i.paidAt || i.createdAt)
    const now = new Date()
    return paid.getMonth() === now.getMonth() && paid.getFullYear() === now.getFullYear()
  }) || []

  const revenue = paidThisMonth.reduce((sum: number, i: any) => sum + parseFloat(i.total || '0'), 0)
  const unpaidBalance = unpaidInvoices.reduce((sum: number, i: any) => sum + parseFloat(i.total || '0'), 0)
  const avgTicket = paidThisMonth.length ? revenue / paidThisMonth.length : 0

  const loading = jobsLoading || quotesLoading || invoicesLoading

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-3xl border border-border p-5 shadow-lg shadow-black/30 md:p-8">
        {/* Hero image background */}
        <div className="pointer-events-none absolute inset-0">
          <img src="/hero-shop.png" alt="" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        <div className="dark-context relative space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-content-secondary">
            <span className="h-2 w-2 rounded-full bg-primary-500" />
            Built for independents, not dealerships
          </div>
          <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
            <div className="space-y-3 lg:col-span-8">
              <h1 className="text-3xl font-extrabold tracking-tight text-content-primary sm:text-4xl">
                Fix cars. <span className="text-primary-400">Get paid.</span>
              </h1>
              <p className="max-w-2xl text-sm text-content-secondary sm:text-base">
                Jobs, quotes, invoices, and customer history in one workflow. Fast enough to run from the bay.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button asChild size={isMobile ? 'sm' : 'md'}>
                  <Link to="/jobs">
                    Open Jobs
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="secondary" size={isMobile ? 'sm' : 'md'}>
                  <Link to="/quotes">Review Quotes</Link>
                </Button>
                <Button asChild variant="secondary" size={isMobile ? 'sm' : 'md'}>
                  <Link to="/">View Landing Page</Link>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:col-span-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs text-content-tertiary">Open Jobs</p>
                {loading ? <Skeleton className="mt-2 h-7 w-12" /> : <p className="mt-1 text-2xl font-bold text-content-primary">{openJobs.length}</p>}
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs text-content-tertiary">Waiting Parts</p>
                {loading ? <Skeleton className="mt-2 h-7 w-12" /> : <p className="mt-1 text-2xl font-bold text-content-primary">{waitingParts.length}</p>}
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs text-content-tertiary">Unpaid Balance</p>
                {loading ? <Skeleton className="mt-2 h-7 w-20" /> : <p className="mt-1 text-xl font-bold text-content-primary">{formatCurrency(unpaidBalance)}</p>}
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs text-content-tertiary">Avg Ticket</p>
                {loading ? <Skeleton className="mt-2 h-7 w-20" /> : <p className="mt-1 text-xl font-bold text-content-primary">{formatCurrency(avgTicket)}</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {[
          { label: 'Open Jobs', value: openJobs.length, icon: Wrench, tone: 'text-info-500' },
          { label: 'Pending Quotes', value: pendingQuotes.length, icon: FileText, tone: 'text-warning-500' },
          { label: 'Unpaid Invoices', value: unpaidInvoices.length, icon: Receipt, tone: 'text-danger-500' },
          { label: 'Paid This Month', value: formatCurrency(revenue), icon: DollarSign, tone: 'text-success-500' },
        ].map((kpi) => (
          <Card key={kpi.label} className="bg-tint/5">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-content-tertiary">{kpi.label}</p>
                  {loading ? (
                    <Skeleton className="mt-2 h-7 w-16" />
                  ) : (
                    <p className="mt-1 text-xl font-bold text-content-primary">{kpi.value}</p>
                  )}
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-tint/5">
                  <kpi.icon className={`h-4 w-4 ${kpi.tone}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current Queue</CardTitle>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 text-xs text-content-tertiary">
              <Clock3 className="h-3.5 w-3.5" />
              Last 5 jobs
            </div>
            <Button asChild variant="link" size="sm">
              <Link to="/jobs">View all</Link>
            </Button>
          </div>
        </CardHeader>
        {jobsLoading ? (
          <div className="space-y-3 p-4 md:p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : recentJobs.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="No jobs yet"
            description="Create your first job to get started"
            action={{ label: 'Open Jobs', onClick: () => {} }}
          />
        ) : isMobile ? (
          <div className="divide-y divide-border">
            {recentJobs.map((job: any) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-tint/5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-content-primary truncate">{getJobDisplayTitle(job)}</p>
                  <p className="text-xs text-content-secondary truncate">
                    {formatVehicleLabel(job.vehicle)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <StatusBadge entity="job" status={job.status} className="ml-3" />
                  {isJobReadyToStart(job) && <ApprovedReadyBadge />}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentJobs.map((job: any) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Link to={`/jobs/${job.id}`} className="font-medium text-content-link hover:underline">
                      {job.vehicle?.customer?.name || 'Unknown Customer'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-content-secondary">
                    {formatVehicleLabel(job.vehicle)}
                  </TableCell>
                  <TableCell className="text-content-secondary">{getJobDisplayTitle(job)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge entity="job" status={job.status} />
                      {isJobReadyToStart(job) && <ApprovedReadyBadge />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}
